import {
  type FhirError,
  fhirErrorResponse,
  isFhirError,
  isZusAPIError,
} from '@ctw/shared/api/fhir/errors';
import { OperationOutcomeModel } from '@ctw/shared/api/fhir/models/operation-outcome';
import { isOperationOutcome } from '@ctw/shared/api/fhir/operation-outcome';

export function isError(error: unknown): error is Error {
  if (typeof error === 'object' && error !== null) {
    return 'message' in error;
  }
  return false;
}

export const errorResponse = (title: string, e?: FhirError | Error | unknown) => {
  if (isFhirError(e)) {
    throw fhirErrorResponse(title, e);
  }

  if (isError(e)) {
    return new Response(JSON.stringify({ ...e, message: e.message, success: false }), {
      status: 500,
      headers: { 'content-type': 'application/json' },
    });
  }

  return new Response(JSON.stringify({ success: false, message: 'Server Error' }), {
    status: 500,
    headers: { 'content-type': 'application/json' },
  });
};

export function errorMessage(error: unknown): string {
  return isError(error) ? error.message.split(':')[0] : 'An unknown error occurred';
}

export function toError(error: unknown): Error {
  if (error instanceof Error) {
    return error;
  }
  return new Error(String(error), { cause: error });
}

/*
 * Convert an unknown thrown value into a plain JSON-friendly object.
 */
export function serializeError(error: unknown): Record<string, unknown> {
  if (error instanceof Error) {
    return {
      name: error.name,
      message: error.message,
      stack: error.stack,
      ...(error.cause !== undefined ? { cause: serializeError(error.cause) } : {}),
      ...Object.fromEntries(Object.entries(error)),
    };
  }
  if (typeof error === 'object' && error !== null) {
    return error as Record<string, unknown>;
  }
  return { message: String(error) };
}

export const getFormResponseErrors = (response: unknown) => {
  let requestErrors: Array<string> = [];
  let responseIsSuccess = true;

  if (isFhirError(response) && isOperationOutcome(response.response.data)) {
    requestErrors = new OperationOutcomeModel(response.response.data).issues
      .filter((issue) => issue.severity !== 'warning')
      .map((issue) => issue.display);
    responseIsSuccess = false;
  } else if (isZusAPIError(response)) {
    requestErrors = [response.response.data.message];
    responseIsSuccess = false;
  } else if (response instanceof Error) {
    requestErrors = [response.message];
    responseIsSuccess = false;
  }
  return { requestErrors, responseIsSuccess };
};
